import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card } from "@/design-system";
import { Icon } from "@/components/Icon";
import { PasswordInput } from "@/components/PasswordInput";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ApiRequestError, api } from "@/api/client";
// Bundler import, same rationale as ForgotPasswordScreen.
import logoMark from "@/design-system/assets/logo-mark.svg";

/**
 * Reset password — the landing view behind the emailed reset link
 * (`/reset-password?token=...`, issue #151/#154).
 *
 * On mount it asks the backend whether the token is still valid
 * (`GET /api/password-resets/:token`): 200 shows the form, 404 shows
 * an "invalid or expired link" state. Submitting POSTs the new password
 * to `/api/password-resets/:token`; every old session dies server-side,
 * so on 204 the user is sent to `/login?reset=1` to sign in fresh.
 */
export function ResetPasswordScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [fieldErrors, setFieldErrors] = useState<
    { password?: string; password_confirmation?: string }
  >({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  // Set when the submission POST answers 404: the token was consumed or
  // reused between the mount-time validity check and the submit, so the
  // link is dead — same "invalid or expired" UI as the check's own 404.
  const [tokenInvalidAfterSubmit, setTokenInvalidAfterSubmit] =
    useState(false);

  // Token validity check. `retry: false` keeps a 404 from being retried
  // pointlessly: an expired link is still expired a second later.
  const check = useQuery({
    queryKey: ["password-resets", token],
    queryFn: () =>
      api.get<{ valid: boolean }>(`/api/password-resets/${token}`, {
        token: null,
      }),
    retry: false,
  });

  const resetMutation = useMutation<
    void,
    Error,
    { password: string; password_confirmation: string }
  >({
    mutationFn: (body) =>
      api.post(`/api/password-resets/${token}`, body, { token: null }),
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (resetMutation.isPending) return;

    setFieldErrors({});
    setGeneralError(null);

    try {
      await resetMutation.mutateAsync({
        password,
        password_confirmation: confirmation,
      });
      // All previous sessions were killed server-side; the user signs
      // in fresh with the new password.
      navigate("/login?reset=1");
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 404) {
        // Token consumed or reused in the meantime (e.g. a second tab).
        setTokenInvalidAfterSubmit(true);
      } else if (err instanceof ApiRequestError && err.status === 422) {
        const errors = err.body?.errors ?? {};
        const next: typeof fieldErrors = {};
        let general: string | null = null;

        for (const [key, raw] of Object.entries(errors)) {
          const messages = Array.isArray(raw) ? raw : [raw];
          const first = messages.find((m) => typeof m === "string");
          if (typeof first !== "string") continue;

          if (key === "password" || key === "password_confirmation") {
            next[key as "password" | "password_confirmation"] = first;
          } else {
            // `detail`-style or unexpected keys land in the banner.
            general = first;
          }
        }

        setFieldErrors(next);
        setGeneralError(general);
      } else {
        setGeneralError(
          err instanceof ApiRequestError
            ? err.message
            : t("resetPassword.errorGeneric"),
        );
      }
    }
  }

  function clearErrorsOnEdit() {
    if (Object.keys(fieldErrors).length > 0 || generalError) {
      setFieldErrors({});
      setGeneralError(null);
    }
  }

  function renderHeader() {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: "var(--space-5)",
        }}
      >
        <img
          src={logoMark}
          alt=""
          width={44}
          height={44}
          style={{ flex: "none" }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              font: "var(--fw-extra) var(--fs-2xl)/1 var(--font-display)",
              color: "var(--text-strong)",
              letterSpacing: "var(--ls-tight)",
            }}
          >
            TheCore Studio
          </div>
          <div
            style={{
              font: "var(--fw-medium) var(--fs-sm)/1 var(--font-body)",
              color: "var(--text-faint)",
              marginTop: 4,
            }}
          >
            {t("auth.tagline")}
          </div>
        </div>
        <LanguageSwitcher />
      </div>
    );
  }

  // Checking the token...
  if (check.isLoading || check.isPending) {
    return (
      <CenterCard>
        {renderHeader()}
        <div
          role="status"
          style={{
            font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
            color: "var(--text-muted)",
          }}
        >
          {t("common.loading")}
        </div>
      </CenterCard>
    );
  }

  // Invalid / expired token — ask for a new link. Reached from the
  // mount-time token check (GET 404) and from a 404 on the submission
  // POST (token consumed or reused in the meantime): either way the
  // link is dead and the UI says so.
  if (
    tokenInvalidAfterSubmit ||
    (check.isError &&
      check.error instanceof ApiRequestError &&
      check.error.status === 404)
  ) {
    return renderInvalidTokenState();
  }

  // Any other failure (network, 5xx) — allow a retry.
  if (check.isError) {
    return (
      <CenterCard>
        {renderHeader()}
        <Card
          role="alert"
          padding="10px 14px"
          style={{
            background: "var(--status-danger-bg)",
            border: "1px solid var(--rust-500)",
            boxShadow: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "var(--status-danger-fg)",
              font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
            }}
          >
            <span style={{ display: "inline-flex", color: "var(--rust-500)" }}>
              <Icon name="flame" size={18} />
            </span>
            {check.error instanceof ApiRequestError
              ? check.error.message
              : t("resetPassword.errorGeneric")}
          </div>
        </Card>
        <Button
          variant="primary"
          onClick={() => {
            check.refetch();
          }}
        >
          {t("common.gotIt")}
        </Button>
      </CenterCard>
    );
  }

  // "Link invalid or expired" state, shared by the check-time and the
  // submit-time 404 so the user always has a path to request a new link.
  function renderInvalidTokenState() {
    return (
      <CenterCard>
        {renderHeader()}
        <div
          style={{
            font: "var(--fw-extra) var(--fs-xl)/1.2 var(--font-display)",
            color: "var(--text-strong)",
            letterSpacing: "var(--ls-tight)",
            marginBottom: 4,
          }}
        >
          {t("resetPassword.invalidTitle")}
        </div>
        <div
          style={{
            font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
            color: "var(--text-muted)",
            marginBottom: "var(--space-5)",
          }}
        >
          {t("resetPassword.invalidBody")}
        </div>
        <Link
          to="/forgot-password"
          id="request-new-link"
          style={{
            font: "var(--fw-semibold) var(--fs-sm)/1 var(--font-body)",
            color: "var(--brand)",
            textDecoration: "none",
            alignSelf: "flex-start",
          }}
        >
          {t("resetPassword.requestNewLink")} →
        </Link>
      </CenterCard>
    );
  }

  // Token valid — the reset form.
  return (
    <CenterCard>
      {renderHeader()}
      <div
        style={{
          font: "var(--fw-extra) var(--fs-xl)/1.2 var(--font-display)",
          color: "var(--text-strong)",
          letterSpacing: "var(--ls-tight)",
          marginBottom: 4,
        }}
      >
        {t("resetPassword.title")}
      </div>

      <form
        id="reset-password-form"
        onSubmit={onSubmit}
        noValidate
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-4)",
          marginTop: "var(--space-4)",
        }}
      >
        <PasswordInput
          label={t("resetPassword.passwordLabel")}
          name="password"
          autoComplete="new-password"
          placeholder={t("resetPassword.passwordPlaceholder")}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            clearErrorsOnEdit();
          }}
          required
          minLength={12}
          disabled={resetMutation.isPending}
          invalid={Boolean(fieldErrors.password)}
          hint={
            fieldErrors.password ?? t("resetPassword.passwordHint")
          }
          iconLeft={<Icon name="settings" size={18} />}
        />
        <PasswordInput
          label={t("resetPassword.confirmLabel")}
          name="password_confirmation"
          autoComplete="new-password"
          placeholder={t("resetPassword.confirmPlaceholder")}
          value={confirmation}
          onChange={(e) => {
            setConfirmation(e.target.value);
            clearErrorsOnEdit();
          }}
          required
          minLength={12}
          disabled={resetMutation.isPending}
          invalid={Boolean(fieldErrors.password_confirmation)}
          hint={fieldErrors.password_confirmation}
          iconLeft={<Icon name="settings" size={18} />}
        />

        {generalError && (
          <Card
            role="alert"
            padding="10px 14px"
            style={{
              background: "var(--status-danger-bg)",
              border: "1px solid var(--rust-500)",
              boxShadow: "none",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: "var(--status-danger-fg)",
                font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
              }}
            >
              <span style={{ display: "inline-flex", color: "var(--rust-500)" }}>
                <Icon name="flame" size={18} />
              </span>
              {generalError}
            </div>
          </Card>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          disabled={resetMutation.isPending}
        >
          {resetMutation.isPending
            ? t("resetPassword.submitting")
            : t("resetPassword.submit")}
        </Button>
      </form>
    </CenterCard>
  );
}

/** Shared centered-card frame used by every state of this screen. */
function CenterCard({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-6) var(--space-4)",
      }}
    >
      <Card
        padding="var(--space-6)"
        style={{ width: "100%", maxWidth: 420 }}
      >
        {children}
      </Card>
    </div>
  );
}